// @flow
import React from "react";
import {
    type DocListColumnTypeEnum,
    DocListColumnType,
    type DocListFilterOperatorsEnum,
    DocListFilterOperators,
    DateTimeFormat,
    FormLookupType,
    DocListFilterType,
} from "data/types";
import LookupEditorControl from "components/ExtraAttributes/EditorControl/LookupEditorControl";
import TextEditorControl from "components/ExtraAttributes/EditorControl/TextEditorControl";
import NumberEditorControl from "components/ExtraAttributes/EditorControl/NumberEditorControl";
import DateEditorControl from "components/ExtraAttributes/EditorControl/DateEditorControl";
import EnumEditorControl from "components/ExtraAttributes/EditorControl/EnumEditorControl";
import RelativeDateEditorControl from "components/SearchForm/RelativeDateEditorControl";
import PrincipalEditorControl from "components/ExtraAttributes/EditorControl/PrincipalEditorControl";

import FileSizeEditorControl from "components/DocListFilterModal/FileSizeEditorControl";

import toFilterEditor from "./toFilterEditor";
import toBetweenEditor from "components/SearchForm/toBetweenEditor";
import loc from "i18next";

type Props = {
    autoFocus?: boolean,
    index: number,
    type: DocListColumnTypeEnum,
    operator: DocListFilterOperatorsEnum,
    value: Array<string>,
    onChange: Function,
    id?: string,
    extra?: any,
};

const FilterLookupEditorControl = toFilterEditor(LookupEditorControl);
const FilterTextEditorControl = toFilterEditor(TextEditorControl);
const FilterNumberEditorControl = toFilterEditor(NumberEditorControl);
const FilterDateEditorControl = toFilterEditor(DateEditorControl);
const FilterEnumEditorControl = toFilterEditor(EnumEditorControl);
const FilterPrincipalEditorControl = toFilterEditor(PrincipalEditorControl);
const FilterRelativeDateEditorControl = toFilterEditor(
    RelativeDateEditorControl
);
const FilterBetweenNumberEditorControl = toFilterEditor(
    toBetweenEditor(NumberEditorControl, NumberEditorControl, true)
);
const FilterBetweenDateEditorControl = toFilterEditor(
    toBetweenEditor(DateEditorControl, DateEditorControl, true)
);

const FilterFileSizeEditorControl = toFilterEditor(FileSizeEditorControl);

const FilterValue = ({
    autoFocus,
    index,
    value,
    operator,
    onChange,
    id,
    extra,
    onOperatorChange,
    filter,
    col,
    name,
    header,
    showLabel,
    formatId,
    isRequired,
}: Props) => {
    const isBetween =
        operator === DocListFilterOperators.Between ||
        operator === DocListFilterOperators.NotBetween;

    const isDecimal =
        col.type === DocListColumnType.DecimalValue ||
        col.type === DocListColumnType.DoubleValue ||
        col.type === DocListColumnType.SingleValue;

    if (extra?.format != null) {
        switch (extra.format) {
            case DocListFilterType.ListCombo:
                return (
                    <FilterLookupEditorControl
                        name={`ip.${col.name}`}
                        lookupType={FormLookupType.ip}
                        header={col.caption}
                        value={
                            value && Array.isArray(value)
                                ? value.map((v) => ({ T: v }))
                                : { T: value }
                        }
                        onChange={
                            /* istanbul ignore next */ (option, i) =>
                                onChange(
                                    option
                                        ? Array.isArray(option)
                                            ? option.map((o) => o?.T)
                                            : option.T
                                        : "",
                                    i
                                )
                        }
                        showLabel={true}
                        col={col}
                        filter={filter}
                        index={index}
                        operator={filter.operator}
                        onOperatorChange={onOperatorChange}
                        isMultiple={
                            filter.operator === DocListFilterOperators.None
                        }
                        isCompact={true}
                        isDoc={true}
                        formatId={formatId}
                        isRequired={isRequired}
                    />
                );
            case DocListFilterType.PrincipalLookup:
                return (
                    <FilterPrincipalEditorControl
                        name={`doclistFilter.principalInput${index}`}
                        header={col.caption}
                        isMulti={
                            filter.operator === DocListFilterOperators.None
                        }
                        isClearable={true}
                        value={value}
                        onChange={
                            /* istanbul ignore next */ (option, i) =>
                                onChange(
                                    Array.isArray(option)
                                        ? option.map((o) => o)
                                        : option,
                                    i
                                )
                        }
                        autoFocus={autoFocus}
                        col={col}
                        filter={filter}
                        index={index}
                        operator={filter.operator}
                        onOperatorChange={onOperatorChange}
                        isCompact={true}
                        isRequired={isRequired}
                    />
                );
            /* istanbul ignore next */
            default:
                break;
        }
    }

    switch (col.type) {
        case DocListColumnType.BooleanValue:
            return (
                <FilterEnumEditorControl
                    name={`doclistFilter.booleanInput${index}`}
                    onChange={onChange}
                    enum={[
                        { value: "True", label: "Yes" },
                        { value: "False", label: "No" },
                    ]}
                    value={value?.[0] || ""}
                    autoFocus={/* istanbul ignore next */ autoFocus || false}
                    className="form-control"
                    id={id}
                    index={index}
                    operator={filter.operator}
                    onOperatorChange={onOperatorChange}
                    filter={filter}
                    col={col}
                    header={col.caption}
                    showLabel={true}
                    isRequired={isRequired}
                />
            );

        case DocListColumnType.DateTimeValue:
            return operator === DocListFilterOperators.DateRelative ? (
                <FilterRelativeDateEditorControl
                    autoFocus={/* istanbul ignore next */ autoFocus || false}
                    index={index}
                    operator={operator}
                    onChange={onChange}
                    id={id}
                    value={value?.[0] || ""}
                    className="form-control"
                    name={`doclistFilter.dateInput${index}`}
                    onOperatorChange={onOperatorChange}
                    filter={filter}
                    col={col}
                    header={col.caption}
                    showLabel={true}
                    type={DateTimeFormat.JsonDateUTC}
                    isRequired={isRequired}
                />
            ) : isBetween ? (
                <FilterBetweenDateEditorControl
                    autoFocus={/* istanbul ignore next */ autoFocus || false}
                    index={index}
                    value={filter.values}
                    operator={operator}
                    onChange={onChange}
                    id={id}
                    className="form-control"
                    name={`doclistFilter.dateInput${index}`}
                    onOperatorChange={onOperatorChange}
                    filter={filter}
                    col={col}
                    header={col.caption}
                    showLabel={true}
                    type={DateTimeFormat.JsonDateUTC}
                    validations={{ isNotInvalid: true }}
                    validationErrors={{
                        isNotInvalid: loc.t("common:validate.date"),
                    }}
                    isRequired={isRequired}
                />
            ) : (
                <FilterDateEditorControl
                    autoFocus={/* istanbul ignore next */ autoFocus || false}
                    index={index}
                    operator={operator}
                    onChange={onChange}
                    id={id}
                    value={value?.[0] || ""}
                    className="form-control"
                    name={`doclistFilter.dateInput${index}`}
                    onOperatorChange={onOperatorChange}
                    filter={filter}
                    col={col}
                    header={col.caption}
                    showLabel={true}
                    type={DateTimeFormat.JsonDate}
                    validations={{ isNotInvalid: true }}
                    validationErrors={{
                        isNotInvalid: loc.t("common:validate.date"),
                    }}
                    isRequired={isRequired}
                />
            );

        case DocListColumnType.FileSize:
            return (
                <FilterFileSizeEditorControl
                    autoFocus={/* istanbul ignore next */ autoFocus || false}
                    value={value?.[0] || ""}
                    onChange={onChange}
                    className="form-control"
                    id={id}
                    name={`doclistFilter.numberInput${index}`}
                    index={index}
                    operator={filter.operator}
                    onOperatorChange={onOperatorChange}
                    filter={filter}
                    col={col}
                    header={col.caption}
                    showLabel={true}
                    isRequired={isRequired}
                />
            );

        case DocListColumnType.IntValue:
        case DocListColumnType.LongValue:
        case DocListColumnType.ShortValue:
        case DocListColumnType.DoubleValue:
        case DocListColumnType.DecimalValue:
        case DocListColumnType.SingleValue:
            return isBetween ? (
                <FilterBetweenNumberEditorControl
                    autoFocus={/* istanbul ignore next */ autoFocus || false}
                    onChange={onChange}
                    decimalScale={isDecimal ? 3 : 0}
                    className="form-control"
                    id={id}
                    name={`doclistFilter.numberInput${index}`}
                    index={index}
                    value={filter.values}
                    operator={filter.operator}
                    onOperatorChange={onOperatorChange}
                    filter={filter}
                    col={col}
                    header={col.caption}
                    showLabel={true}
                    isRequired={isRequired}
                />
            ) : (
                <FilterNumberEditorControl
                    autoFocus={/* istanbul ignore next */ autoFocus || false}
                    value={value?.[0] || ""}
                    onChange={onChange}
                    decimalScale={isDecimal ? 3 : 0}
                    className="form-control"
                    id={id}
                    name={`doclistFilter.numberInput${index}`}
                    index={index}
                    operator={filter.operator}
                    onOperatorChange={onOperatorChange}
                    filter={filter}
                    col={col}
                    header={col.caption}
                    showLabel={true}
                    isRequired={isRequired}
                />
            );

        default:
            return (
                <FilterTextEditorControl
                    autoFocus={/* istanbul ignore next */ autoFocus || false}
                    value={value?.[0] || ""}
                    onChange={onChange}
                    className="form-control"
                    id={id}
                    name={`doclistFilter.textInput${index}`}
                    index={index}
                    operator={filter.operator}
                    onOperatorChange={onOperatorChange}
                    filter={filter}
                    col={col}
                    header={col.caption}
                    showLabel={true}
                    isRequired={isRequired}
                />
            );
    }
};
FilterValue.displayName = "FilterValue";
export default FilterValue;
